/**
 * Box Office Projections Data Types and Validation
 * TypeScript-style interfaces and validation schemas for Box Office Projections API
 */

// ============================================================================
// CORE DATA TYPES
// ============================================================================

/**
 * Box Office Projection Data Structure
 * @typedef {Object} BoxOfficeProjection
 * @property {string} id - Unique projection identifier
 * @property {string} filmId - IMDB ID of the film (format: ttXXXXXXX)
 * @property {string} filmTitle - Title of the film
 * @property {string} projectionDate - Date when projection was made (ISO 8601)
 * @property {number} outlierMinimum - Farthest floor expectation in USD
 * @property {number} actualProjection - Most likely outcome in USD
 * @property {number} outlierCeiling - Farthest ceiling expectation in USD
 * @property {number} projectionFloor - Calculated floor (actual * 0.9)
 * @property {number} projectionCeiling - Calculated ceiling (actual * 1.1)
 * @property {number} daysUntilRelease - Days between projection and release
 * @property {number} weeksUntilRelease - Weeks until release (days / 7)
 * @property {string} createdBy - User who created the projection
 * @property {string} createdAt - Creation timestamp (ISO 8601)
 * @property {string} updatedAt - Last update timestamp (ISO 8601)
 * @property {boolean} isActive - Whether projection is active
 * @property {string} [notes] - Optional notes about the projection
 */

/**
 * Box Office Projection Creation Request
 * @typedef {Object} CreateProjectionRequest
 * @property {string} filmId - IMDB ID of the film (required)
 * @property {string} projectionDate - Date when projection was made (required)
 * @property {number} outlierMinimum - Farthest floor expectation in USD (required)
 * @property {number} actualProjection - Most likely outcome in USD (required)
 * @property {number} outlierCeiling - Farthest ceiling expectation in USD (required)
 * @property {string} [notes] - Optional notes (max 500 characters)
 */

/**
 * Box Office Projection Update Request
 * @typedef {Object} UpdateProjectionRequest
 * @property {string} [projectionDate] - Updated projection date
 * @property {number} [outlierMinimum] - Updated outlier minimum
 * @property {number} [actualProjection] - Updated actual projection
 * @property {number} [outlierCeiling] - Updated outlier ceiling
 * @property {string} [notes] - Updated notes
 */

/**
 * Box Office Projections Response
 * @typedef {Object} BoxOfficeProjectionsResponse
 * @property {boolean} success - Whether request was successful
 * @property {BoxOfficeProjection[]} data - Array of projections
 * @property {PaginationInfo} pagination - Pagination information
 * @property {ResponseMetadata} metadata - Response metadata
 */

/**
 * Single Box Office Projection Response
 * @typedef {Object} BoxOfficeProjectionResponse
 * @property {boolean} success - Whether request was successful
 * @property {BoxOfficeProjection} data - Projection data
 * @property {ResponseMetadata} metadata - Response metadata
 */

/**
 * Film Box Office Projections Response
 * @typedef {Object} FilmBoxOfficeProjectionsResponse
 * @property {boolean} success - Whether request was successful
 * @property {Object} data - Film projections data
 * @property {string} data.filmId - Film ID
 * @property {string} data.filmTitle - Film title
 * @property {BoxOfficeProjection[]} data.projections - Array of projections
 * @property {number} data.totalProjections - Total number of projections
 * @property {PaginationInfo} pagination - Pagination information
 */

/**
 * Pagination Information
 * @typedef {Object} PaginationInfo
 * @property {number} total - Total number of items
 * @property {number} limit - Number of items per page
 * @property {number} offset - Number of items skipped
 * @property {number} totalPages - Total number of pages
 * @property {boolean} hasNext - Whether there are more pages
 * @property {boolean} hasPrev - Whether there are previous pages
 */

/**
 * Response Metadata
 * @typedef {Object} ResponseMetadata
 * @property {string} [filmReleaseDate] - Film release date
 * @property {Object} [calculations] - Calculation formulas used
 * @property {string[]} [updatedFields] - Fields that were updated
 * @property {string[]} [recalculatedFields] - Fields that were recalculated
 * @property {Object} [filters] - Applied filters
 * @property {Object} [sort] - Applied sorting
 */

// ============================================================================
// QUERY PARAMETERS
// ============================================================================

/**
 * Box Office Projections Query Parameters
 * @typedef {Object} ProjectionsQueryParams
 * @property {string} [filmId] - Filter by specific film ID
 * @property {string} [projectionDateFrom] - Filter projections from this date
 * @property {string} [projectionDateTo] - Filter projections to this date
 * @property {string} [createdBy] - Filter by creator
 * @property {boolean} [isActive] - Filter by active status
 * @property {number} [limit] - Number of results (1-100, default: 20)
 * @property {number} [offset] - Number of results to skip (default: 0)
 * @property {string} [sortBy] - Sort field (projection_date, created_at, actual_projection)
 * @property {string} [sortOrder] - Sort order (asc, desc, default: desc)
 */

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate IMDB ID format
 * @param {string} filmId - Film ID to validate
 * @returns {Object} Validation result
 */
export const validateFilmId = (filmId) => {
  if (!filmId || typeof filmId !== 'string') {
    return { isValid: false, error: 'Film ID is required' };
  }
  
  const imdbIdRegex = /^tt\d{7,8}$/;
  if (!imdbIdRegex.test(filmId)) {
    return { isValid: false, error: 'Film ID must be in format ttXXXXXXX' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate projection date
 * @param {string} date - Date to validate
 * @returns {Object} Validation result
 */
export const validateProjectionDate = (date) => {
  if (!date || typeof date !== 'string') {
    return { isValid: false, error: 'Projection date is required' };
  }
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }
  
  // Check if date is not in the future (allow past dates for backfilling)
  const now = new Date();
  now.setHours(23, 59, 59, 999); // Allow dates up to end of today
  if (dateObj > now) {
    return { isValid: false, error: 'Projection date cannot be in the future' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate currency amount
 * @param {number} amount - Amount to validate
 * @param {string} fieldName - Name of the field for error messages
 * @returns {Object} Validation result
 */
export const validateCurrencyAmount = (amount, fieldName = 'Amount') => {
  if (amount === null || amount === undefined || amount === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  // Convert string to number if needed
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numericAmount)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }
  
  if (numericAmount < 0) {
    return { isValid: false, error: `${fieldName} cannot be negative` };
  }
  
  if (numericAmount > 1000000000) { // 1 billion USD
    return { isValid: false, error: `${fieldName} cannot exceed $1,000,000,000` };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate notes field
 * @param {string} notes - Notes to validate
 * @returns {Object} Validation result
 */
export const validateNotes = (notes) => {
  if (notes === null || notes === undefined) {
    return { isValid: true, error: null }; // Notes are optional
  }
  
  if (typeof notes !== 'string') {
    return { isValid: false, error: 'Notes must be a string' };
  }
  
  if (notes.length > 500) {
    return { isValid: false, error: 'Notes cannot exceed 500 characters' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Validate create projection request
 * @param {CreateProjectionRequest} request - Request to validate
 * @returns {Object} Validation result
 */
export const validateCreateProjectionRequest = (request) => {
  const errors = [];
  
  // Validate film ID
  const filmIdValidation = validateFilmId(request.filmId);
  if (!filmIdValidation.isValid) {
    errors.push({ field: 'filmId', message: filmIdValidation.error });
  }
  
  // Validate projection date
  const dateValidation = validateProjectionDate(request.projectionDate);
  if (!dateValidation.isValid) {
    errors.push({ field: 'projectionDate', message: dateValidation.error });
  }
  
  // Validate currency amounts
  const outlierMinValidation = validateCurrencyAmount(request.outlierMinimum, 'Outlier minimum');
  if (!outlierMinValidation.isValid) {
    errors.push({ field: 'outlierMinimum', message: outlierMinValidation.error });
  }
  
  const actualValidation = validateCurrencyAmount(request.actualProjection, 'Actual projection');
  if (!actualValidation.isValid) {
    errors.push({ field: 'actualProjection', message: actualValidation.error });
  }
  
  const outlierMaxValidation = validateCurrencyAmount(request.outlierCeiling, 'Outlier ceiling');
  if (!outlierMaxValidation.isValid) {
    errors.push({ field: 'outlierCeiling', message: outlierMaxValidation.error });
  }
  
  // Validate logical relationships
  if (outlierMinValidation.isValid && actualValidation.isValid && outlierMaxValidation.isValid) {
    if (request.outlierMinimum > request.actualProjection) {
      errors.push({ field: 'outlierMinimum', message: 'Outlier minimum cannot be greater than actual projection' });
    }
    
    if (request.actualProjection > request.outlierCeiling) {
      errors.push({ field: 'actualProjection', message: 'Actual projection cannot be greater than outlier ceiling' });
    }
    
    if (request.outlierMinimum > request.outlierCeiling) {
      errors.push({ field: 'outlierMinimum', message: 'Outlier minimum cannot be greater than outlier ceiling' });
    }
  }
  
  // Validate notes
  const notesValidation = validateNotes(request.notes);
  if (!notesValidation.isValid) {
    errors.push({ field: 'notes', message: notesValidation.error });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    error: errors.length > 0 ? errors[0].message : null
  };
};

/**
 * Validate update projection request
 * @param {UpdateProjectionRequest} request - Request to validate
 * @returns {Object} Validation result
 */
export const validateUpdateProjectionRequest = (request) => {
  const errors = [];
  
  // Validate projection date if provided
  if (request.projectionDate !== undefined) {
    const dateValidation = validateProjectionDate(request.projectionDate);
    if (!dateValidation.isValid) {
      errors.push({ field: 'projectionDate', message: dateValidation.error });
    }
  }
  
  // Validate currency amounts if provided
  if (request.outlierMinimum !== undefined) {
    const outlierMinValidation = validateCurrencyAmount(request.outlierMinimum, 'Outlier minimum');
    if (!outlierMinValidation.isValid) {
      errors.push({ field: 'outlierMinimum', message: outlierMinValidation.error });
    }
  }
  
  if (request.actualProjection !== undefined) {
    const actualValidation = validateCurrencyAmount(request.actualProjection, 'Actual projection');
    if (!actualValidation.isValid) {
      errors.push({ field: 'actualProjection', message: actualValidation.error });
    }
  }
  
  if (request.outlierCeiling !== undefined) {
    const outlierMaxValidation = validateCurrencyAmount(request.outlierCeiling, 'Outlier ceiling');
    if (!outlierMaxValidation.isValid) {
      errors.push({ field: 'outlierCeiling', message: outlierMaxValidation.error });
    }
  }
  
  // Validate notes if provided
  if (request.notes !== undefined) {
    const notesValidation = validateNotes(request.notes);
    if (!notesValidation.isValid) {
      errors.push({ field: 'notes', message: notesValidation.error });
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    error: errors.length > 0 ? errors[0].message : null
  };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format currency amount for display
 * @param {number} amount - Amount to format
 * @param {string} [currency] - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'N/A';
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @param {Object} [options] - Formatting options
 * @returns {string} Formatted date string
 */
export const formatProjectionDate = (dateInput, options = {}) => {
  if (!dateInput) return 'N/A';
  
  // Handle API date object format: { value: "2025-10-10" }
  let dateString = dateInput;
  if (typeof dateInput === 'object' && dateInput.value) {
    dateString = dateInput.value;
  }
  
  // Handle YYYY-MM-DD format (backend format) by appending time
  let parsedDateString = dateString;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    parsedDateString = `${dateString}T00:00:00`;
  }
  
  const date = new Date(parsedDateString);
  if (isNaN(date.getTime())) {
    console.warn('Failed to parse date:', dateString, 'from input:', dateInput);
    return 'Invalid Date';
  }
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Calculate days until release
 * @param {string} projectionDate - Projection date
 * @param {string} releaseDate - Release date
 * @returns {number} Days until release
 */
export const calculateDaysUntilRelease = (projectionDate, releaseDate) => {
  if (!projectionDate || !releaseDate) return 0;
  
  // Handle API date object format: { value: "2025-10-10" }
  const getDateValue = (dateInput) => {
    if (typeof dateInput === 'object' && dateInput.value) {
      return dateInput.value;
    }
    return dateInput;
  };
  
  const projDateValue = getDateValue(projectionDate);
  const relDateValue = getDateValue(releaseDate);
  
  const projDate = new Date(projDateValue);
  const relDate = new Date(relDateValue);
  
  if (isNaN(projDate.getTime()) || isNaN(relDate.getTime())) return 0;
  
  const diffTime = relDate.getTime() - projDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate weeks until release
 * @param {number} daysUntilRelease - Days until release
 * @returns {number} Weeks until release (rounded to 1 decimal)
 */
export const calculateWeeksUntilRelease = (daysUntilRelease) => {
  return Math.round((daysUntilRelease / 7) * 10) / 10;
};

/**
 * Get projection confidence level based on range
 * @param {number} actualProjection - Actual projection amount
 * @param {number} outlierMinimum - Outlier minimum
 * @param {number} outlierCeiling - Outlier ceiling
 * @returns {string} Confidence level (high, medium, low)
 */
export const getProjectionConfidence = (actualProjection, outlierMinimum, outlierCeiling) => {
  if (!actualProjection || !outlierMinimum || !outlierCeiling) return 'unknown';
  
  const range = outlierCeiling - outlierMinimum;
  const rangePercentage = (range / actualProjection) * 100;
  
  if (rangePercentage <= 50) return 'high';
  if (rangePercentage <= 100) return 'medium';
  return 'low';
};

/**
 * Get projection trend direction
 * @param {BoxOfficeProjection[]} projections - Array of projections sorted by date
 * @returns {string} Trend direction (up, down, stable)
 */
export const getProjectionTrend = (projections) => {
  if (!projections || projections.length < 2) return 'stable';
  
  const latest = projections[0];
  const previous = projections[1];
  
  const change = latest.actualProjection - previous.actualProjection;
  const changePercentage = (change / previous.actualProjection) * 100;
  
  if (changePercentage > 5) return 'up';
  if (changePercentage < -5) return 'down';
  return 'stable';
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const boxOfficeTypes = {
  // Validation functions
  validateFilmId,
  validateProjectionDate,
  validateCurrencyAmount,
  validateNotes,
  validateCreateProjectionRequest,
  validateUpdateProjectionRequest,
  
  // Utility functions
  formatCurrency,
  formatProjectionDate,
  calculateDaysUntilRelease,
  calculateWeeksUntilRelease,
  getProjectionConfidence,
  getProjectionTrend
};

export default boxOfficeTypes;
